import get from 'lodash/get';

export const EXCLUDED_VULN_TABLE_FIELDS = [
  'description',
  'data',
  'request',
  'response',
  'resolution'
];

const TABLE_TO_API_COLUMN = {
  asset: 'target',
  confirmed: 'confirmed',
  create_date: 'date',
  cve: 'cve',
  cvss: 'cvss3',
  cwe: 'cwe',
  easeofresolution: 'easeofresolution',
  evidence: 'attachments_count',
  external_id: 'external_id',
  host__os: 'host_os',
  hostnames: 'hostnames',
  id: 'id',
  impact: 'impact',
  issuetracker: 'issuetracker',
  last_detected: 'last_detected',
  method: 'method',
  name: 'name',
  owasp: 'owasp',
  path: 'path',
  query: 'query',
  risk: 'risk',
  service__name: 'service',
  severity: 'severity',
  status: 'status',
  status_code: 'status_code',
  status_history: 'status_history',
  tool: 'tool',
  update_date: 'update_date',
  web: 'type',
  website: 'website'
};

const SUPPORTED_VULN_COLUMNS = new Set([
  'id',
  'confirmed',
  'name',
  'severity',
  'tags',
  'risk',
  'cvss2',
  'cvss3',
  'cvss4',
  'hostnames',
  'date',
  'cve',
  'cwe',
  'path',
  'owasp',
  'service',
  'tool',
  'status',
  'issuetracker',
  'target',
  'easeofresolution',
  'external_id',
  'method',
  'query',
  'status_code',
  'website',
  'host_os',
  'impact',
  'update_date',
  'type'
]);

const UNSUPPORTED_VULN_QUERY_FIELDS = ['workspace_name', 'workspace__name'];
const REQUIRED_VULN_COLUMNS = ['id'];

const isUnsupportedVulnQueryField = (field = '') => UNSUPPORTED_VULN_QUERY_FIELDS.includes(field);
const isSupportedVulnColumn = (column = '') => SUPPORTED_VULN_COLUMNS.has(column);
const getApiVulnColumn = (field = '') => TABLE_TO_API_COLUMN[field];

export const hasUnsupportedVisibleVulnColumns = (fields = {}) => Object.keys(fields).some((field) => {
  if (!get(fields, `${field}.visible`, false) || field === 'selected') return false;

  if (field.endsWith('_custom')) return true;

  const apiColumn = getApiVulnColumn(field);
  return !apiColumn || !isSupportedVulnColumn(apiColumn);
});

const sanitizeVulnQueryParam = (queryParam = {}) => {
  const sanitizedQueryParam = { ...queryParam };

  if (Array.isArray(queryParam.order_by)) {
    sanitizedQueryParam.order_by = queryParam.order_by
      .filter((orderBy) => !isUnsupportedVulnQueryField(get(orderBy, 'field', '')));
  }

  if (Array.isArray(queryParam.group_by)) {
    sanitizedQueryParam.group_by = queryParam.group_by
      .filter((groupBy) => !isUnsupportedVulnQueryField(get(groupBy, 'field', '')));
  }

  return sanitizedQueryParam;
};

export const getVisibleVulnColumns = (fields = {}) => {
  const hasUnsupportedVisibleColumn = hasUnsupportedVisibleVulnColumns(fields);

  // If the UI needs a visible column the backend cannot return via `columns`
  // (for example `workspace_name`, `status_history` or any custom field),
  // we must skip the optimization and keep requesting the full payload.
  if (hasUnsupportedVisibleColumn) return [];

  const visibleColumns = Object.keys(fields)
    .filter((field) => get(fields, `${field}.visible`, false) && isSupportedVulnColumn(getApiVulnColumn(field)))
    .sort((a, b) => {
      const aOrder = get(fields, `${a}.order`, 0);
      const bOrder = get(fields, `${b}.order`, 0);
      return aOrder - bOrder;
    })
    .map((field) => getApiVulnColumn(field));

  return [...new Set(visibleColumns)];
};

export const withVulnColumns = (queryParam, fields = {}, options = {}) => {
  if (!queryParam || Array.isArray(queryParam)) return queryParam;
  const sanitizedQueryParam = sanitizeVulnQueryParam(queryParam);

  if (options.keepFullPayload) return sanitizedQueryParam;

  const visibleColumns = getVisibleVulnColumns(fields);
  const groupByColumns = get(sanitizedQueryParam, 'group_by', [])
    .map((group) => get(group, 'field', ''))
    .filter(Boolean);
  if (visibleColumns.length === 0 && groupByColumns.length === 0) return sanitizedQueryParam;

  const columns = [...REQUIRED_VULN_COLUMNS, ...visibleColumns, ...groupByColumns];

  return {
    ...sanitizedQueryParam,
    columns: [...new Set(columns)]
  };
};
